import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { APP_URL_CHAT } from '../../../const';
import useGoToCheckout from '../../../hooks/useGoToCheckout';
import { isPremiumSubscriber } from '../../../service/advisorAccess';
import { getUser, getUserPaymentPopupState, toggleAuthModal } from '../../../store/slices/authSlice';
import {
    getPaymentModalDataLoading,
    getPaymentModalOrigin,
    getPaymentModalSource,
    getPaymentModalState,
    setShowPaymentModal
} from '../../../store/slices/modalsSlice';
import { getFeaturedSubscriptionPlan, getSubscriptionPlans } from '../../../store/slices/paymentsSlice';
import { gaEvents } from '../../../utils/analytics';
import Modal from '../../common/Modal';
import PaymentButton, { paymentBtnType } from '../../common/PaymentButton';
import Spinner from '../../common/Spinner';
import IssuePaymentContent from './popupContent/IssuePaymentContent';
import MessageLimitPaywall from './popupContent/MessageLimitPaywall';
import AskWithoutLimitsPaywall from './popupContent/MessageLimitPaywall/AskWithoutLimitsPaywall';
import paywallStyles from './popupContent/MessageLimitPaywall/styles.module.scss';
import OfferPaymentContent from './popupContent/OfferPaymentContent';
import PlanPaymentContent from './popupContent/PlanPaymentContent';
import ProductPaymentContent from './popupContent/ProductPaymentContent';
import TrialPaymentContent from './popupContent/TrialPaymentContent';
import styles from './styles.module.scss';

// HORO-10563: sessionStorage key + TTL for the anonymous "pick a plan, then
// register" resume below — same shape/reasoning as ZaraUpsellModal's own
// 'zara_pending_subscribe_upsell', generalized to carry whichever advisor
// alias opened this (paymentModalOrigin) instead of a hardcoded Zara.
const PENDING_ADVISOR_SUBSCRIBE_KEY = 'advisor_pending_subscribe_upsell';
const PENDING_ADVISOR_SUBSCRIBE_TTL_MS = 30 * 60 * 1000;

const PaymentModal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const goToCheckout = useGoToCheckout();
    const { t } = useTranslation();
    const isShow = useSelector(getPaymentModalState);
    const user = useSelector(getUser);
    const advisorOrigin = useSelector(getPaymentModalOrigin);
    const paymentSource = useSelector(getPaymentModalSource);
    const paymentState = useSelector(getUserPaymentPopupState);
    const plan = useSelector(getFeaturedSubscriptionPlan);
    const plans = useSelector(getSubscriptionPlans);
    const isOfferLoading = useSelector(getPaymentModalDataLoading);
    const isOffer = !!plan?.isOffer;

    // HORO-10563: this modal used to only ever mount for authenticated users
    // (see App.js) — a non-Zara advisor's SUBSCRIBE lock reason only ever
    // applied to a *registered* non-premium viewer (HORO-10562). A later fix
    // to getAdvisorLockReason correctly extended SUBSCRIBE to anonymous
    // viewers too (an advisor outside both the anon- and registered-
    // accessible rosters, e.g. Aviva), so this can now open for someone with
    // no account at all — `paymentState` (trial/plan/product/issue) is
    // derived entirely from a real user's plan/limits and means nothing for
    // a guest, so that whole derivation is skipped in favor of the same
    // "Ask without limits" paywall the registered/plan case shows, with a
    // register-then-resume CTA instead of straight-to-checkout (a protected
    // route an anonymous user can't reach). Mirrors ZaraUpsellModal's own
    // anonymous path almost exactly, generalized to whichever advisor's
    // alias opened it instead of a hardcoded Zara.
    useEffect(() => {
        if (!user?.id || typeof sessionStorage === 'undefined') return;
        const stored = sessionStorage.getItem(PENDING_ADVISOR_SUBSCRIBE_KEY);
        if (!stored) return;
        sessionStorage.removeItem(PENDING_ADVISOR_SUBSCRIBE_KEY);
        let parsed;
        try {
            parsed = JSON.parse(stored);
        } catch {
            return;
        }
        if (!parsed?.alias || Number.isNaN(parsed.at) || Date.now() - parsed.at > PENDING_ADVISOR_SUBSCRIBE_TTL_MS) {
            return;
        }
        // Registering alone never unlocks this advisor — that's the whole
        // reason this path exists instead of the plain REGISTER gate —
        // except when they signed into an existing account that already has
        // a subscription. Same premium-check-on-resume ZaraUpsellModal does,
        // sending them straight into a chat instead of checkout.
        if (isPremiumSubscriber(user)) {
            navigate(`/${APP_URL_CHAT}/${parsed.alias}`);
            return;
        }
        gaEvents.onPaymentPopupBtn(paymentBtnType.subscription);
        goToCheckout(parsed.paymentData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // HORO-10635: denominator for conversion rate per subscription source.
    useEffect(() => {
        // zaraUpsell is skipped: ZaraUpsellModal already counted this paywall, and its
        // subscribe button only continues into this modal. loveTarot opens this modal directly.
        if (isShow && paymentSource !== 'zaraUpsell') gaEvents.paywallViewed(paymentSource || 'unknown');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow]);

    const contentConfig = {
        trial: isOffer ? <OfferPaymentContent /> : <TrialPaymentContent />,
        plan: isOffer ? <OfferPaymentContent /> : <PlanPaymentContent />,
        product: <ProductPaymentContent />,
        issue: isOffer ? <OfferPaymentContent /> : <IssuePaymentContent />
    };

    // The out-of-messages paywall (Figma 4167-16380) is a full-screen takeover,
    // not a bottom sheet, so it renders outside `Modal` rather than fighting its
    // sheet layout. The offer variant still uses the sheet.
    const isMessageLimitPaywall = paymentState === 'plan' && !isOffer;

    const content = useMemo(() => {
        return isOfferLoading ? <Spinner fill={'#fff'} /> : contentConfig[paymentState];
    }, [paymentState, isOffer, isOfferLoading]);

    const onCloseBtn = () => {
        dispatch(setShowPaymentModal(false));
    };

    // Anonymous users can't reach `/checkout` (a protected route), so the
    // plan they picked is stashed and replayed by the effect above once
    // they've registered.
    const onAnonUpsellTap = (paymentData) => {
        dispatch(setShowPaymentModal(false));
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(
                PENDING_ADVISOR_SUBSCRIBE_KEY,
                JSON.stringify({
                    at: Date.now(),
                    alias: advisorOrigin,
                    paymentData: { ...paymentData, origin: advisorOrigin, source: paymentSource }
                })
            );
        }
        // Own context ('advisorSubscribe'), not 'advisorLocked': 'advisorLocked'
        // is in App.js's post-login redirect whitelist (keyed off
        // STORAGE_KEY_PENDING_ADVISOR_ALIAS, which this flow never sets), and
        // reusing it would race that redirect against this component's own
        // resume effect above. See REGISTER_UPSELL_VARIANT_BY_CONTEXT
        // (const.js) for the copy this context maps to.
        dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'advisorSubscribe' }));
    };

    if (!isShow) return null;

    if (!user?.id) {
        return (
            <AskWithoutLimitsPaywall
                plans={plans}
                onClose={onCloseBtn}
                renderCta={(selected) => (
                    <PaymentButton
                        text={t('pages.paywall.continue')}
                        type={paymentBtnType.subscription}
                        plan={selected}
                        extraClasses={[paywallStyles.cta]}
                        onClick={onAnonUpsellTap}
                    />
                )}
            />
        );
    }

    if (isMessageLimitPaywall) return isOfferLoading ? <Spinner fill={'#fff'} /> : <MessageLimitPaywall />;

    return <Modal children={content} onCloseClick={!isOffer && onCloseBtn} innerStyles={isOffer ? [styles.offerModal] : []} />;
};

export default PaymentModal;
